import { isDevAuthMode, syntheticDevSession } from "./devAuth";

// Session helpers consumed by route guards + components.
//
// Post-Supabase: the auth state lives in an HttpOnly `__Host-session`
// cookie that JavaScript cannot read. The SPA learns its auth status by
// hitting GET /api/me/session — 200 means signed in, 401 means signed out.
//
// When VITE_AUTH_MODE === "stub", we short-circuit the network call and
// return a synthetic session keyed off VITE_DEV_USER_ID. The Pages Functions
// and the services-cf Workers have parallel guards keyed off AUTH_MODE=stub.

export interface CurrentUser {
	id: string;
}

export interface CurrentSession {
	user: CurrentUser;
}

export async function getCurrentUser(): Promise<CurrentUser | null> {
	if (isDevAuthMode()) {
		return syntheticDevSession().user;
	}
	try {
		const res = await fetch("/api/me/session", {
			credentials: "include",
			headers: { Accept: "application/json" },
		});
		if (res.status === 401) return null;
		if (!res.ok) {
			console.error("getCurrentUser", res.status, res.statusText);
			return null;
		}
		const body = (await res.json()) as { userId?: string };
		if (!body.userId) return null;
		return { id: body.userId };
	} catch (err) {
		console.error("getCurrentUser", err);
		return null;
	}
}

export async function getCurrentSession(): Promise<CurrentSession | null> {
	const user = await getCurrentUser();
	return user ? { user } : null;
}

export async function signOut(): Promise<void> {
	if (isDevAuthMode()) {
		// No-op in stub mode — there's no real session to revoke.
		return;
	}
	try {
		await fetch("/api/auth/signout", {
			method: "POST",
			credentials: "include",
		});
	} catch (err) {
		console.error("signOut", err);
	}
	window.location.replace("/auth/login");
}

// Subscribe to auth state changes. Returns an unsubscribe function.
//
// Post-Supabase there's no realtime auth-state stream — the cookie either
// exists or it doesn't, and we learn that by polling /api/me/session. To
// keep the existing callsite contract working, we fire the callback once on
// the next microtask with the current state and return a no-op unsubscribe.
// Components that need to re-check after a sign-in/sign-out flow re-run
// getCurrentSession() themselves.
export function onAuthStateChange(
	cb: (session: CurrentSession | null) => void,
): () => void {
	const noop = (): void => undefined;
	if (isDevAuthMode()) {
		queueMicrotask(() => {
			cb({ user: syntheticDevSession().user });
		});
		return noop;
	}
	queueMicrotask(() => {
		void getCurrentSession().then(cb);
	});
	return noop;
}
